import { useEffect, useState } from 'react';
import CampaignImageField from './CampaignImageField';
import CampaignPreview from './CampaignPreview';
import { sanitizeStorefrontObjectPosition } from './storefrontObjectPosition';
import {
  campaignHasEmailChannel,
  datetimeLocalToIso,
  hydrateInAppEditForm,
} from './inAppDisplayEditable';

const emptyEditForm = () => ({
  title: '',
  message: '',
  imageUrl: '',
  ctaLabel: '',
  ctaUrl: '',
  storefrontDisplayUntil: '',
  storefrontObjectPosition: { x: 42, y: 40 },
});

/**
 * Modification affichage In-App vitrine active — pas d’envoi / pas de quota.
 */
export default function CampaignEditInAppDisplayModal({
  isOpen,
  onClose,
  campaign,
  commerceName,
  onSave,
  saving,
  error,
}) {
  const [form, setForm] = useState(emptyEditForm);

  useEffect(() => {
    if (isOpen && campaign) {
      setForm(hydrateInAppEditForm(campaign));
    }
    if (!isOpen) {
      setForm(emptyEditForm());
    }
  }, [isOpen, campaign]);

  if (!isOpen) return null;

  const hasEmail = campaignHasEmailChannel(campaign);
  const previewForm = {
    channels: ['IN_APP'],
    title: form.title,
    message: form.message,
    imageUrl: form.imageUrl,
    ctaLabel: form.ctaLabel,
    ctaUrl: form.ctaUrl,
  };

  const canSave =
    form.title.trim() &&
    form.message.trim() &&
    form.storefrontDisplayUntil &&
    !saving;

  return (
    <div className="fixed inset-0 z-[5000] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} aria-hidden />
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-5xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-700">
            <h3 className="text-xl font-semibold text-white">Modifier l’affichage In-App</h3>
            <button type="button" className="text-white/90 hover:text-white text-sm" onClick={onClose}>
              Fermer
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
            <div className="lg:col-span-3 p-6 space-y-5 border-b lg:border-b-0 lg:border-r border-gray-100">
              {hasEmail ? (
                <p className="text-xs text-sky-900 bg-sky-50 border border-sky-100 rounded-md px-3 py-2">
                  Seul l’affichage In-App sera modifié. L’email déjà envoyé restera inchangé.
                </p>
              ) : null}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 font-sans"
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
              </div>

              <CampaignImageField
                imageUrl={form.imageUrl || ''}
                onChange={(url) =>
                  // Functional update: upload puis reset focal synchrone ne doivent
                  // jamais s’écraser (spread stale {…form} perdait imageUrl).
                  setForm((prev) => ({ ...prev, imageUrl: url || '' }))
                }
                showInAppFormatHint
                showStorefrontFocal
                storefrontObjectPosition={form.storefrontObjectPosition}
                onStorefrontObjectPositionChange={(pos) =>
                  setForm((prev) => ({ ...prev, storefrontObjectPosition: pos }))
                }
              />

              <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                <p className="text-sm font-medium text-gray-800">Bouton d’action (optionnel)</p>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Libellé</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    value={form.ctaLabel || ''}
                    onChange={(e) => setForm({ ...form, ctaLabel: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Lien</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    value={form.ctaUrl || ''}
                    onChange={(e) => setForm({ ...form, ctaUrl: e.target.value })}
                    placeholder="/votre-boutique ou https://…"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fin d’affichage vitrine
                </label>
                <input
                  type="datetime-local"
                  className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md text-sm"
                  value={form.storefrontDisplayUntil}
                  onChange={(e) => setForm({ ...form, storefrontDisplayUntil: e.target.value })}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Vous pouvez prolonger ou raccourcir la période. Les clients ayant fermé le bandeau
                  ne le reverront pas automatiquement.
                </p>
              </div>

              {error ? (
                <div className="rounded-md bg-red-50 border border-red-100 text-red-700 text-sm px-3 py-2">
                  {error}
                </div>
              ) : null}

              <div className="lg:hidden pt-2 border-t border-gray-100">
                <CampaignPreview form={previewForm} commerceName={commerceName} />
              </div>
            </div>

            <div className="hidden lg:block lg:col-span-2 p-6 bg-gray-50">
              <CampaignPreview form={previewForm} commerceName={commerceName} />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-100 flex flex-wrap justify-end gap-3 bg-white">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              Annuler
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={!canSave}
              onClick={() =>
                onSave({
                  title: form.title.trim(),
                  message: form.message.trim(),
                  // Tri-state côté API : null = clear explicite ; URL = set.
                  // Toujours envoyer la valeur courante du formulaire (jamais omise ici).
                  imageUrl: form.imageUrl ? form.imageUrl : null,
                  ctaLabel: form.ctaLabel || '',
                  ctaUrl: form.ctaUrl || '',
                  storefrontDisplayUntil: datetimeLocalToIso(form.storefrontDisplayUntil),
                  storefrontObjectPosition: sanitizeStorefrontObjectPosition(
                    form.storefrontObjectPosition
                  ),
                })
              }
            >
              {saving ? 'Enregistrement…' : 'Enregistrer l’affichage'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
